// Permission Context for TrackProfit RBAC
// Provides permission checking and role-based UI filtering

import React, { createContext, useContext, useState, useEffect } from 'react';
import { dataService } from '../services/dataService';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const PERMISSION_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

  const getPermissionCacheKey = (uid, accId) => {
    if (!uid && !accId) return null;
    return `permissions:${uid || 'anonymous'}:${accId || 'default'}`;
  };

  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [lastLoadTime, setLastLoadTime] = useState(0);



  // Load permissions when accountId is available
  useEffect(() => {
    if (accountId) {
      loadPermissionsFromCache();
    } else {
      // If no accountId, set loading to false
      setLoading(false);
    }
  }, [accountId, userId]);

  // Listen for user data changes via custom events
  useEffect(() => {
    const handleUserUpdate = (event) => {
      // Custom event fired when user data is updated
      if (event.detail && event.detail.userData) {
        // Set accountId from user data if not already set
        const userData = event.detail.userData;
        if (userData.accountId && !accountId) {
          setAccountId(userData.accountId);
        } else if (accountId) {
          // Only reload permissions if we already have an accountId
          loadPermissionsFromCache();
        }
        const resolvedUserId = userData?.id || userData?.userId || userData?.uid || null;
        if (resolvedUserId) {
          setUserId(resolvedUserId);
        }
      }
    };

    const handleUserLogout = (event) => {
      // Custom event fired when user logs out
      if (event.detail && event.detail.loggedOut) {
        // Clear permissions immediately
        setPermissions([]);
        setUserRole(null);
        setAccountId(null);
        setLoading(false);
        setError(null);
      }
    };

    // Listen for custom events (same-tab)
    window.addEventListener('tp_user_updated', handleUserUpdate);
    window.addEventListener('tp_user_logout', handleUserLogout);
    
    return () => {
      window.removeEventListener('tp_user_updated', handleUserUpdate);
      window.removeEventListener('tp_user_logout', handleUserLogout);
    };
  }, [accountId]);


  // Load subscription status
  const loadSubscriptionStatus = async () => {
    try {
      setSubscriptionLoading(true);
      
      // Import userApi dynamically to avoid circular dependencies
      const { userApi } = await import('../api');
      const status = await userApi.subscriptionStatus();
      
      setSubscriptionStatus(status);
      setSubscriptionLoading(false);
    } catch (error) {
      setSubscriptionLoading(false);
    }
  };


  const loadPermissionsFromCache = async () => {
    // Debounce rapid permission loads (minimum 1 second between loads)
    const now = Date.now();
    if (now - lastLoadTime < 1000) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setLastLoadTime(now);

      // Check if user is authenticated
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        // No auth token, clear permissions and log out
        setPermissions([]);
        setUserRole(null);
        setLoading(false);
        localStorage.removeItem('authToken');
        window.location.reload();
        return;
      }

      const cacheKey = getPermissionCacheKey(userId, accountId);
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
      let cachedApplied = false;

      if (cacheKey) {
        try {
          const cached = await dataService.getMetadata(cacheKey);
          if (cached && Array.isArray(cached.permissions)) {
            setPermissions(cached.permissions);
            setUserRole(cached.role || null);
            cachedApplied = true;
            const cacheAge = now - (cached.cachedAt || 0);
            if (!isOnline || cacheAge <= PERMISSION_CACHE_TTL) {
              setLoading(false);
              if (!isOnline) {
                return;
              }
            }
          }
        } catch (cacheError) {
          console.warn('Permission cache read failed', cacheError);
        }
      }

      if (!isOnline && !cachedApplied) {
        setPermissions([]);
        setUserRole(null);
        setLoading(false);
        return;
      }

      // Fetch fresh permissions from server
      await fetchPermissionsFromServer({ cacheKey });
      
    } catch (error) {
      setError(error.message);
      setPermissions([]);
      setUserRole(null);
      setLoading(false);
    }
  };

  // Fetch permissions from server
  const fetchPermissionsFromServer = async ({ cacheKey } = {}) => {
    try {
      // Import userApi dynamically to avoid circular dependencies
      const { userApi } = await import('../api');
      
      // Fetch fresh user data from server
      const userData = await userApi.me();
      
      if (userData && userData.permissions && userData.role) {
        // Set accountId from user data if not already set
        // Only update accountId if it's different to prevent circular dependency
        if (userData.accountId && userData.accountId !== accountId) {
          setAccountId(userData.accountId);
        }
        const resolvedUserId = userData?.id || userData?.userId || userData?.uid || null;
        if (resolvedUserId) {
          setUserId(resolvedUserId);
        }
        
        // Set permissions
        setPermissions(userData.permissions);
        setUserRole(userData.role);

        if (cacheKey) {
          try {
            await dataService.setMetadata(cacheKey, {
              permissions: userData.permissions,
              role: userData.role,
              cachedAt: Date.now()
            });
          } catch (cacheError) {
            console.warn('Permission cache write failed', cacheError);
          }
        }
      } else {
        // No permissions available
        setPermissions([]);
        setUserRole(null);
      }
      
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setPermissions([]);
      setUserRole(null);
      setLoading(false);
    }
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    // If no permission required, allow access
    if (!permission) return true;
    
    // If still loading permissions, deny access for security
    if (loading) {
      return false;
    }
    
    // Admin users have all permissions
    if (permissions.includes('*')) {
      return true;
    }
    
    const hasAccess = permissions.includes(permission);
    return hasAccess;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionList) => {
    if (!Array.isArray(permissionList) || permissionList.length === 0) {
      return false;
    }
    
    return permissionList.some(permission => hasPermission(permission));
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissionList) => {
    if (!Array.isArray(permissionList) || permissionList.length === 0) {
      return false;
    }
    
    return permissionList.every(permission => hasPermission(permission));
  };

  // Check if user can perform an action on a resource
  const canPerformAction = (action, resource) => {
    const permission = `${resource}:${action}`;
    return hasPermission(permission);
  };

  // Check if user can view a resource
  const canView = (resource) => {
    return hasPermission(`${resource}:view`);
  };

  // Check if user can create a resource
  const canCreate = (resource) => {
    return hasPermission(`${resource}:create`);
  };

  // Check if user can edit a resource
  const canEdit = (resource) => {
    return hasPermission(`${resource}:edit`);
  };

  // Check if user can delete a resource
  const canDelete = (resource) => {
    return hasPermission(`${resource}:delete`);
  };

  // Check if user can manage a resource (full access)
  const canManage = (resource) => {
    return hasPermission(`${resource}:manage`) || hasPermission('*');
  };

  // Check if user is org admin
  const isOrgAdmin = () => {
    return userRole === 'ORG_ADMIN' || permissions.includes('*');
  };

  // Check if user is billing role
  const isBilling = () => {
    return userRole === 'BILLING';
  };

  // Check if user is viewer role
  const isViewer = () => {
    return userRole === 'VIEWER';
  };

  // Get user's role name
  const getRoleName = () => {
    const roleNames = {
      'ORG_ADMIN': 'Organization Admin',
      'BILLING': 'Billing',
      'VIEWER': 'Viewer',
      'SALES': 'Sales',
      'ACCOUNTANT': 'Accountant',
      'INVENTORY_MANAGER': 'Inventory Manager'
    };
    
    return roleNames[userRole] || 'Unknown';
  };

  // Refresh permissions (useful after role changes)
  const refreshPermissions = async () => {
    await loadPermissionsFromCache();
  };

  // Clear permissions (useful on logout)
  const clearPermissions = () => {
    setPermissions([]);
    setUserRole(null);
    setAccountId(null);
    setError(null);
  };


  const value = {
    // State
    permissions,
    userRole,
    accountId,
    loading,
    error,
    subscriptionStatus,
    subscriptionLoading,
    
    // Permission checking methods
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canPerformAction,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canManage,
    
    // Role checking methods
    isOrgAdmin,
    isBilling,
    isViewer,
    getRoleName,
    
    // Utility methods
    refreshPermissions,
    clearPermissions
  };


  // Clean up any old debug references
  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.debugPermissions) {
      delete window.debugPermissions;
    }
  }, []);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// Higher-order component for permission-based rendering
export const withPermissions = (WrappedComponent, requiredPermissions = []) => {
  return function PermissionWrappedComponent(props) {
    const { hasAnyPermission, loading } = usePermissions();
    
    if (loading) {
      return <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
        <span className="ml-2 text-gray-600">Loading permissions...</span>
      </div>;
    }
    
    if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
      return (
        <div className="text-center p-8">
          <div className="text-red-600 text-4xl mb-4">🚫</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to access this feature.</p>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};

// Hook for conditional rendering based on permissions
export const usePermissionRender = (permission, fallback = null) => {
  const { hasPermission } = usePermissions();
  
  return hasPermission(permission) ? true : fallback;
};

export default PermissionContext;
